import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_scorecards: number
  active_scorecards: number
  departments_covered: number
  avg_score: number
  top_performers: number
  improvement_needed: number
}

interface Scorecard {
  id: string
  department: string
  financial: number
  customer: number
  process: number
  learning: number
  overall: number
}

interface Perspective {
  perspective: string
  weight: number
  avg_score: number
  trend: string
}

export default function PerformanceScorecardsPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [scorecards, setScorecards] = useState<Scorecard[]>([])
  const [perspectives, setPerspectives] = useState<Perspective[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, scorecardsRes, perspRes] = await Promise.all([
          fetch('/api/performancescorecards/statistics'),
          fetch('/api/performancescorecards/scorecards'),
          fetch('/api/performancescorecards/perspectives')
        ])
        setStatistics(await statsRes.json())
        setScorecards(await scorecardsRes.json())
        setPerspectives(await perspRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Performance Scorecards...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Performance Scorecards</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_scorecards}</div>
              <div className="text-sm text-gray-500">Total Scorecards</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_scorecards}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.departments_covered}</div>
              <div className="text-sm text-gray-500">Departments</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.avg_score}</div>
              <div className="text-sm text-gray-500">Avg Score</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.top_performers}</div>
              <div className="text-sm text-gray-500">Top Performers</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{statistics.improvement_needed}</div>
              <div className="text-sm text-gray-500">Needs Improvement</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Department Scorecards</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Financial</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Process</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Learning</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Overall</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {scorecards.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{s.department}</td>
                      <td className="px-4 py-3 text-gray-600">{s.financial}</td>
                      <td className="px-4 py-3 text-gray-600">{s.customer}</td>
                      <td className="px-4 py-3 text-gray-600">{s.process}</td>
                      <td className="px-4 py-3 text-gray-600">{s.learning}</td>
                      <td className="px-4 py-3 font-semibold text-blue-600">{s.overall}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Perspectives</h2></div>
            <div className="p-4 space-y-4">
              {perspectives.map((p, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{p.perspective}</span>
                    <span className="text-sm text-gray-500">Weight: {p.weight}%</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span>Avg Score</span>
                      <span>{p.avg_score}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{width: `${p.avg_score}%`}}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
